/**
 * Attach command - reconnect to an existing running session
 *
 * Unlike `launch`, this does NOT create the session.
 * It connects to an already-running session via WebSocket terminal.
 * Use --destroy to tear down the session on disconnect.
 *
 * Accepts a session ID or name from the local registry.
 */

import chalk from 'chalk';
import { SANDBOX_WORKER_URL, DEFAULT_API_URL } from '@spacecode/shared/remote';
import { resolveAuthToken, sandboxClientOptions } from '../config.js';
import { loadSessions, resolveSession, markSessionDestroyed } from '../sessions.js';

export interface AttachOptions {
  destroy?: boolean;
}

export async function attach(idOrName: string, options: AttachOptions): Promise<void> {
  console.log(chalk.bold('\nspacecode attach\n'));

  // ── Resolve name to ID ────────────────────────────────────────────────

  let sessionId = idOrName;
  const sessions = await loadSessions();
  const session = resolveSession(sessions, idOrName);

  if (session) {
    sessionId = session.id;
    const label = session.name ? `"${session.name}" (${session.id})` : session.id;
    console.log(chalk.dim(`Session: ${label}`));
  }

  // ── Auth ─────────────────────────────────────────────────────────────

  console.log(chalk.dim('Authenticating...'));
  const token = await resolveAuthToken();

  // ── Backend name fallback ─────────────────────────────────────────────
  // If no local session found and idOrName isn't a hex ID, try backend name lookup
  if (!session && !/^[0-9a-f]+$/i.test(idOrName)) {
    try {
      const res = await fetch(`${DEFAULT_API_URL}/api/sandboxes/by-name/${encodeURIComponent(idOrName)}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(5_000),
      });
      if (res.ok) {
        const data = await res.json() as { id: string; name?: string };
        if (typeof data.id === 'string' && data.id) {
          sessionId = data.id;
          console.log(chalk.dim(`Resolved "${idOrName}" → ${sessionId}`));
        }
      } else if (res.status === 404) {
        console.error(chalk.red(`\nSession "${idOrName}" not found.`));
        console.log(chalk.dim('Run `spacecode list` to see available sessions.'));
        process.exit(1);
      } else if (res.status === 401 || res.status === 403) {
        console.error(chalk.red(`\nAuthentication failed (${res.status}). Please run \`spacecode auth login\` to refresh your credentials.`));
        process.exit(1);
      } else if (res.status >= 500) {
        console.log(chalk.yellow(`Warning: Backend name lookup failed (${res.status}). Name resolution may be incomplete.`));
      } else {
        console.log(chalk.dim(`Backend name lookup returned ${res.status}, falling back to ID`));
      }
    } catch (err) {
      console.log(chalk.dim(`Backend name lookup unavailable: ${err instanceof Error ? err.message : 'network error'}`));
    }
  }

  const { createSandboxClient, SandboxApiError } = await import('@spacecode/sandbox-client');
  const client = createSandboxClient(SANDBOX_WORKER_URL, token, sandboxClientOptions(token));

  // ── Check session status ──────────────────────────────────────────────

  console.log(chalk.dim(`Checking session ${sessionId}...`));

  try {
    const info = await client.getSandboxStatus(sessionId);

    if (info.status !== 'running') {
      console.error(chalk.red(`\nSession ${sessionId} is not running (status: ${info.status}).`));
      console.log(chalk.dim('Only running sessions can be attached to.'));
      process.exit(1);
    }

    console.log(chalk.green(`Session ${sessionId} is running.`));
  } catch (error) {
    if (error instanceof SandboxApiError && error.status === 404) {
      console.error(chalk.red(`\nSession '${idOrName}' not found.`));
      if (!session) {
        console.log(chalk.dim('Run `spacecode list` to see available sessions.'));
      }
      process.exit(1);
    }
    console.error(chalk.red(`\nError checking session: ${error instanceof Error ? error.message : String(error)}`));
    process.exit(1);
  }

  // Connect via WebSocket terminal
  const wsUrl = client.getWsUrl(sessionId);
  console.log(chalk.dim('Connecting via WebSocket...\n'));
  console.log(chalk.dim('─'.repeat(50)));

  try {
    const { connectTerminal } = await import('../utils/ws-terminal.js');
    const exitCode = await connectTerminal(wsUrl, sessionId);

    console.log(chalk.dim('─'.repeat(50)));
    console.log(chalk.dim('\nSession ended.'));

    if (options.destroy) {
      console.log(chalk.dim('Destroying session...'));
      try {
        await client.destroySandbox(sessionId);
        await markSessionDestroyed(sessionId).catch((err) => {
          console.warn(chalk.dim(`Warning: Could not update session registry: ${err instanceof Error ? err.message : String(err)}`));
        });
        console.log(chalk.green('Session destroyed.'));
      } catch (err) {
        console.log(chalk.yellow(`Warning: Failed to destroy session: ${err instanceof Error ? err.message : String(err)}`));
      }
    } else {
      const reconnectLabel = session?.name ?? sessionId;
      console.log(chalk.dim(`Session still running (no web UI yet; ADR-15).`));
      console.log(chalk.dim(`Or reconnect: spacecode attach ${reconnectLabel}`));
      console.log(chalk.dim(`Destroy: spacecode destroy ${reconnectLabel}`));
    }

    process.exit(exitCode);
  } catch (error) {
    console.error(chalk.red('\nError: WebSocket connection failed.'));
    console.error(chalk.dim(String(error)));
    process.exit(1);
  }
}
